import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader, Check } from "lucide-react";
import { RiskCashSale, useListRiskCashSales, useActionCashSaleRows, ActionCashSales } from "@/lib/api/risk/cashSales/general";
import { formatNumberToDisplay } from "@/lib/api/common";
import moment from "moment";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { DialogDescription } from "@radix-ui/react-dialog";

interface UpdateCashSalesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRow: RiskCashSale | null;
    onSuccess: () => void;
}

export function UpdateCashSalesDialog({ isOpen, onClose, selectedRow, onSuccess }: UpdateCashSalesDialogProps) {
    const { toast } = useToast();
    const actionCashSaleRows = useActionCashSaleRows();
    const [selectedBaseId, setSelectedBaseId] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Reset selected base ID when dialog opens/closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedBaseId(null);
            setIsUpdating(false);
        }
    }, [isOpen]);

    // Fetch rows with same contract number and release that have no flag status
    const { data: potentialBaseRows, isLoading } = useListRiskCashSales({
        filters: {
            contract_number: selectedRow?.contract_number,
            contract_release: selectedRow?.contract_release,
            flag_status: [""], // Empty array means no flag status (null)
            is_enabled: true,
        }
    });

    const validBaseRows = potentialBaseRows?.data?.data || [];

    const handleUpdate = async () => {
        if (!selectedRow || !selectedBaseId) return;

        setIsUpdating(true);
        try {
            const result = await actionCashSaleRows({
                action: ActionCashSales.Update,
                data: {
                    ids: [selectedRow.id],
                    update_info: {
                        base_id: selectedBaseId
                    }
                }
            });

            if (!result.success) {
                throw result;
            }

            toast({ title: "Updated successfully" });
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Failed to update cash sale", error);
            toast({
                title: "Failed to update cash sale",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    if (!selectedRow) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Update Cash Sale - Select Base Row</DialogTitle>
                </DialogHeader>
                <DialogDescription className="text-muted-foreground text-xs">
                    Merge with a confirmed row
                </DialogDescription>

                <div className="flex-1 overflow-hidden flex flex-col space-y-4">
                    {/* Selected row info */}
                    <Card className="border-2 border-primary">
                        <CardContent className="pt-4">
                            <h3 className="font-semibold mb-2 text-sm">Selected Row (To be merged)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                    <span className="text-muted-foreground">Customer:</span>{" "}
                                    <span className="font-medium">{selectedRow.Customer?.long_name || selectedRow.report_customer_id}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Commodity:</span>{" "}
                                    <span className="font-medium">{selectedRow.commodity}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Contract #:</span>{" "}
                                    <span className="font-medium">{selectedRow.contract_number}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Contract Release:</span>{" "}
                                    <span className="font-medium">{selectedRow.contract_release}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Quantity:</span>{" "}
                                    <span className="font-medium">{formatNumberToDisplay(selectedRow.quantity)}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Price:</span>{" "}
                                    <span className="font-medium">{formatNumberToDisplay(selectedRow.price)}</span>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">Crop Year:</span>{" "}
                                    <span className="font-medium">{selectedRow.crop_year}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Separator />

                    {/* Available base rows */}
                    <div className="flex-1 overflow-hidden">
                        <h3 className="font-semibold mb-2">Select Confirmed Row</h3>
                        
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader className="h-6 w-6 animate-spin" />
                            </div>
                        ) : validBaseRows.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No valid base rows found for this contract
                            </div>
                        ) : (
                            <ScrollArea className="h-[400px] pr-4">
                                <div className="space-y-2">
                                    {validBaseRows.map((row: RiskCashSale) => (
                                        <Card
                                            key={row.id}
                                            className={`cursor-pointer transition-colors ${
                                                selectedBaseId === row.id
                                                    ? "border-primary bg-primary/5"
                                                    : "hover:bg-muted/50"
                                            }`}
                                            onClick={() => !isUpdating && setSelectedBaseId(row.id)}
                                        >
                                            <CardContent className="py-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm flex-1">
                                                        <div>
                                                            <span className="text-muted-foreground">Customer:</span>{" "}
                                                            <span className="font-medium">{row.Customer?.long_name || row.report_customer_id}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Status:</span>{" "}
                                                            <Badge variant="outline" className="text-xs">{row.status}</Badge>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Market Zone:</span>{" "}
                                                            <span className="font-medium">{row.report_market_zone_id}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Quantity:</span>{" "}
                                                            <span className="font-medium">{formatNumberToDisplay(row.quantity)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Sched Qty:</span>{" "}
                                                            <span className="font-medium">{formatNumberToDisplay(row.scheduled_quantity)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Balance:</span>{" "}
                                                            <span className="font-medium">{formatNumberToDisplay(row.balance)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Price:</span>{" "}
                                                            <span className="font-medium">{formatNumberToDisplay(row.price)}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">Start Date:</span>{" "}
                                                            <span className="font-medium">{moment(row.start_date).format("MM/DD/YYYY")}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-muted-foreground">End Date:</span>{" "}
                                                            <span className="font-medium">{moment(row.end_date).format("MM/DD/YYYY")}</span>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant={selectedBaseId === row.id ? "default" : "outline"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            !isUpdating && setSelectedBaseId(row.id);
                                                        }}
                                                        disabled={isUpdating}
                                                        className="ml-4"
                                                    >
                                                        {selectedBaseId === row.id ? (
                                                            <>
                                                                <Check className="h-4 w-4 mr-1" />
                                                                Selected
                                                            </>
                                                        ) : (
                                                            "Select"
                                                        )}
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdate}
                        disabled={!selectedBaseId || isUpdating}
                    >
                        {isUpdating ? (
                            <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            "Update"
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
